import axios, { AxiosResponse, CancelToken } from "axios"
import { GetDocumentoComercialImportadoByIdDtoProps } from "./DTOs/GetDocumentoComercialImportadoByIdDto"
import {
  GetDocumentoComercialImportadoByIdAdapter,
  GetDocumentosComercialesImportadosByFiltersAdapter,
} from "./DocumentoImportado.adapter"
import { GetDocumentosComercialesImportadosByFiltersDtoIPagedEnumerableProps } from "./Schemas/GetDocumentosComercialesImportadosByFiltersDtoIPagedEnumerable"
import { SortingCriteria } from "./interfaces/SortingCriteria"

/**
 * Retrieves a document by its ID from the imported commercial documents.
 *
 * @param id - The ID of the document to retrieve.
 * @returns A promise that resolves to the retrieved document.
 */
export async function GetDocumentoComercialImportadoById(
  id: string
): Promise<GetDocumentoComercialImportadoByIdDtoProps> {
  const { data }: Record<"data", GetDocumentoComercialImportadoByIdDtoProps> =
    await axios.get(`/DocumentoImportado/${id}`)
  return GetDocumentoComercialImportadoByIdAdapter(data)
}

/**
 * Posts multiple files as DocumentoComercial and returns the responses.
 * @param files - An array of File objects to be posted.
 * @returns A Promise that resolves to an array of AxiosResponse objects.
 */
export async function postDocumentoComercial(
  files: File[]
): Promise<AxiosResponse[]> {
  const requests: Promise<AxiosResponse>[] = files.map(async (file: File) => {
    const bodyFormData = new FormData()
    bodyFormData.append(
      "file",
      new Blob([file], { type: file.type }),
      file.name
    )
    const res = await axios.post(
      "/DocumentoImportado/procesar-documentocomercial",
      bodyFormData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    )
    return res
  })

  const responses = await Promise.all(requests)
  return responses
}

export interface GetDocumentosComercialesImportadosByFiltersParamsProps {
  SortingCriteria: SortingCriteria
  IsEnabled: boolean
  MaxResultCount: number
  SkipCount: number
}

/**
 * Retrieves documentos comerciales importados based on the specified filters.
 * @param queryParams - The parameters used to filter the documents.
 *   - `SortingCriteria`: To sort the documents ASC | DESC by a specific criteria
 *   - `MaxResultCount`: The maximum number of documents to retrieve per page (pagination).
 *   - `SkipCount`: The number of documents to skip (pagination).
 *   - `IsEnabled`: Whether the pagination is enabled or not
 * @returns A promise that resolves to the response containing the filtered documents.
 */
export async function GetDocumentosComercialesImportadosByFilters(
  queryParams: GetDocumentosComercialesImportadosByFiltersParamsProps,
  cancelToken?: CancelToken
): Promise<GetDocumentosComercialesImportadosByFiltersDtoIPagedEnumerableProps> {
  const {
    data,
  }: {
    data: GetDocumentosComercialesImportadosByFiltersDtoIPagedEnumerableProps
  } = await axios.get(`/DocumentoImportado`, {
    params: queryParams,
    cancelToken,
  })
  return {
    currentPage: data.currentPage,
    items: data.items?.map((documento) =>
      GetDocumentosComercialesImportadosByFiltersAdapter(documento)
    ),
    totalCount: data.totalCount,
  }
}

/**
 * Deletes a documento comercial with the specified ID.
 * @param id - The ID of the documento comercial to delete.
 * @returns A Promise that resolves to the AxiosResponse object containing the response data.
 */
export async function deleteDocumentoComercial(
  id: string
): Promise<AxiosResponse> {
  const response = await axios.delete(`/DocumentoImportado/${id}`)
  return response
}
